/**
 * Expandable Screen
 *
 * Full-screen morphing overlay for user-triggered marketing actions.
 *
 * Primary responsibilities:
 * - Morph a trigger surface into a branded full-screen panel
 * - Lock background scroll while expanded
 * - Provide escape and close controls for accessibility
 *
 * Dependencies:
 * - framer-motion
 * - react-dom
 * - Radix Slot
 * - lucide-react
 * - lib/utils
 */

'use client'

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Slot } from '@radix-ui/react-slot'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

interface ExpandableScreenContextValue {
  animationDuration: number
  collapse: () => void
  contentRadius: string
  expand: () => void
  isExpanded: boolean
  layoutId: string
  triggerRadius: string
}

interface ExpandableScreenProps {
  animationDuration?: number
  children: ReactNode
  contentRadius?: string
  defaultExpanded?: boolean
  layoutId?: string
  lockScroll?: boolean
  onExpandChange?: (expanded: boolean) => void
  triggerRadius?: string
}

interface ExpandableScreenTriggerProps {
  children: ReactNode
  className?: string
}

interface ExpandableScreenContentProps {
  children: ReactNode
  className?: string
  closeButtonClassName?: string
  showCloseButton?: boolean
}

interface ExpandableScreenBackgroundProps {
  className?: string
  content?: ReactNode
  trigger?: ReactNode
}

const ExpandableScreenContext = createContext<ExpandableScreenContextValue | null>(null)

export function useExpandableScreen() {
  const context = useContext(ExpandableScreenContext)

  if (!context) {
    throw new Error('useExpandableScreen must be used inside ExpandableScreen')
  }

  return context
}

// --------------

export function ExpandableScreen({
  animationDuration = 0.32,
  children,
  contentRadius = '28px',
  defaultExpanded = false,
  layoutId = 'expandable-screen',
  lockScroll = true,
  onExpandChange,
  triggerRadius = '999px'
}: ExpandableScreenProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const setExpanded = useCallback(
    (expanded: boolean) => {
      setIsExpanded(expanded)
      onExpandChange?.(expanded)
    },
    [onExpandChange]
  )
  const expand = useCallback(() => setExpanded(true), [setExpanded])
  const collapse = useCallback(() => setExpanded(false), [setExpanded])
  const contextValue = useMemo<ExpandableScreenContextValue>(
    () => ({
      animationDuration,
      collapse,
      contentRadius,
      expand,
      isExpanded,
      layoutId,
      triggerRadius
    }),
    [
      animationDuration,
      collapse,
      contentRadius,
      expand,
      isExpanded,
      layoutId,
      triggerRadius
    ]
  )

  useEffect(() => {
    if (!isExpanded || !lockScroll) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isExpanded, lockScroll])

  useEffect(() => {
    if (!isExpanded) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        collapse()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [collapse, isExpanded])

  return (
    <ExpandableScreenContext.Provider value={contextValue}>
      {children}
    </ExpandableScreenContext.Provider>
  )
}

// --------------

export function ExpandableScreenTrigger({
  children,
  className
}: ExpandableScreenTriggerProps) {
  const { animationDuration, expand, isExpanded, layoutId, triggerRadius } =
    useExpandableScreen()
  const shouldReduceMotion = useReducedMotion()

  if (isExpanded) {
    return null
  }

  return (
    <motion.div
      className={cn('inline-flex', className)}
      layoutId={shouldReduceMotion ? undefined : layoutId}
      onClick={expand}
      style={{ borderRadius: triggerRadius }}
      transition={{ duration: shouldReduceMotion ? 0 : animationDuration, ease: 'easeInOut' }}
    >
      <Slot>{children}</Slot>
    </motion.div>
  )
}

// --------------

export function ExpandableScreenContent({
  children,
  className,
  closeButtonClassName,
  showCloseButton = true
}: ExpandableScreenContentProps) {
  const {
    animationDuration,
    collapse,
    contentRadius,
    isExpanded,
    layoutId
  } = useExpandableScreen()
  const shouldReduceMotion = useReducedMotion()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (isExpanded) {
      contentRef.current?.focus()
    }
  }, [isExpanded, isMounted])

  if (!isMounted) {
    return null
  }

  return createPortal(
    <AnimatePresence>
      {isExpanded ? (
        <motion.div
          aria-modal="true"
          className="fixed inset-0 z-[999] flex items-center justify-center overflow-y-auto bg-slate-50 p-3 sm:p-5 dark:bg-slate-950"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          role="dialog"
          transition={{ duration: shouldReduceMotion ? 0 : 0.18 }}
        >
          <motion.div
            className={cn(
              'relative min-h-[calc(100dvh-1.5rem)] w-full max-w-[1120px] overflow-hidden border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(244,247,251,0.96))] shadow-[0_34px_100px_rgba(15,23,42,0.24)] outline-none sm:min-h-[min(860px,calc(100dvh-2.5rem))] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(11,16,24,0.98),rgba(5,10,16,0.98))] dark:shadow-[0_34px_100px_rgba(0,0,0,0.52)]',
              className
            )}
            layoutId={shouldReduceMotion ? undefined : layoutId}
            ref={contentRef}
            style={{ borderRadius: contentRadius }}
            tabIndex={-1}
            transition={{ duration: shouldReduceMotion ? 0 : animationDuration, ease: 'easeInOut' }}
          >
            <div className="pointer-events-none absolute inset-0 opacity-45 [background-image:linear-gradient(135deg,rgba(148,163,184,0.12)_0_1px,transparent_1px_9px)] dark:opacity-10" />
            {showCloseButton ? (
              <button
                aria-label="Close expanded screen"
                className={cn(
                  'bg-white/88 absolute right-4 top-4 z-20 inline-flex size-11 items-center justify-center rounded-full border border-slate-200/80 text-slate-700 shadow-[0_12px_30px_rgba(15,23,42,0.1)] transition-colors hover:bg-white hover:text-slate-950 dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/15',
                  closeButtonClassName
                )}
                onClick={collapse}
                type="button"
              >
                <X className="size-5" />
              </button>
            ) : null}
            <div className="relative z-10 min-h-[inherit]">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  )
}

// --------------

export function ExpandableScreenBackground({
  className,
  content,
  trigger
}: ExpandableScreenBackgroundProps) {
  const { isExpanded } = useExpandableScreen()

  return <div className={className}>{isExpanded ? content : trigger}</div>
}
